import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ArrowLeft, MessageCircle, Pencil, Mail, Send, X, ExternalLink, Loader2, Download } from "lucide-react";
import api from "../api";
import { DOC } from "../components/print/DocumentShell";
import { createDocumentPdfFile } from "../utils/documentPdf";
import { useAuth, can } from "../context/AuthContext";
const PdfPreview = lazy(() => import("../components/print/PdfPreview"));

const KIND_TO_URL = { credit_note: "returns", debit_note: "returns", sale: "sales", purchase: "purchases", quotation: "quotations", letter: "letters", proforma: "proformas", challan: "challans", rate_contract: "tenders/contracts" };
const KIND_TO_BACK = { credit_note: "/returns", debit_note: "/returns", sale: "/sales", purchase: "/purchases", quotation: "/quotations", letter: "/letters", proforma: "/proformas", challan: "/challans", rate_contract: "/tenders/contracts" };

const documentLabel = (kind, doc) => (
  kind === "credit_note" ? "Credit Note" :
  kind === "debit_note" ? "Debit Note" :
  kind === "sale" ? "Tax Invoice" :
  kind === "purchase" ? "Purchase Bill" :
  kind === "quotation" ? "Rate Quotation" :
  kind === "proforma" ? "Proforma Invoice" :
  kind === "challan" ? "Delivery Challan" :
  kind === "rate_contract" ? "Rate Contract" :
  doc?.letter_type || "Letter"
);

const documentNumber = (doc) => doc?.ret_no || doc?.bill_no || doc?.quote_no || doc?.contract_no || doc?.ref_no || "SGP-Document";

const safePdfName = (kind, doc) => {
  const base = `${documentLabel(kind, doc)}_${documentNumber(doc)}`.replace(/[^A-Za-z0-9._-]+/g, "_");
  return `${base}.pdf`;
};

export default function InvoicePrint() {
  const { kind, id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const pendingPdf = useRef(null);
  const pdfPreviewRef = useRef(null);
  const [copy, setCopy] = useState("Original for Recipient");
  const [shareOpen, setShareOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [mailStatus, setMailStatus] = useState(null);
  const [emailMode, setEmailMode] = useState("direct");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [whatsAppStatus, setWhatsAppStatus] = useState(null);
  const [whatsAppTo, setWhatsAppTo] = useState("");
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setDoc(null);
    setLoadError("");
    api.get(`/${KIND_TO_URL[kind] || "sales"}/${id}`, { signal: controller.signal })
      .then((r) => { if (!controller.signal.aborted) setDoc(r.data); })
      .catch((err) => { if (!controller.signal.aborted) setLoadError(err.message || "Unable to load document"); });
    return () => controller.abort();
  }, [kind, id]);

  useEffect(() => {
    if (!doc) return;
    const no = documentNumber(doc);
    const label = documentLabel(kind, doc);
    const recipientEmail = doc.hospital?.email || doc.company?.email || doc.organisation?.email || "";
    const recipientPhone = doc.hospital?.phone || doc.company?.phone || doc.organisation?.phone || "";
    setEmailTo(recipientEmail);
    setWhatsAppTo(recipientPhone);
    const issuer = doc.seller?.trade_name || doc.seller?.name || "Company";
    setEmailSubject(`${label} ${no} - ${issuer}`);
    setEmailMessage(`Dear Sir/Madam,\n\nPlease find attached ${label} ${no} from ${issuer}.\n\nRegards,\n${issuer}`);
  }, [doc, kind]);

  useEffect(() => {
    if (!doc) return;
    let active = true;
    let url = "";
    const controller = new AbortController();
    setPdfFile(null);
    setPreviewUrl("");
    setPdfError("");
    setGeneratingPdf(true);
    const promise = createDocumentPdfFile(kind, id, copy, safePdfName(kind, doc), controller.signal);
    pendingPdf.current = promise;
    promise.then((file) => {
      if (!active) return;
      setPdfFile(file);
      url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }).catch((err) => {
      if (active) setPdfError(err.message || "Unable to generate PDF");
    }).finally(() => { if (active) setGeneratingPdf(false); });
    return () => {
      active = false;
      controller.abort();
      if (url) URL.revokeObjectURL(url);
      if (pendingPdf.current === promise) pendingPdf.current = null;
    };
  }, [doc, kind, id, copy]);

  useEffect(() => {
    if (!emailOpen) return;
    api.get("/mail/status").then((r) => {
      setMailStatus(r.data);
      if (r.data?.direct_mode) setEmailMode("direct");
    }).catch(() => setMailStatus({ configured: false, outlook_connected: false }));
  }, [emailOpen]);

  useEffect(() => {
    if (!shareOpen) return;
    api.get("/whatsapp/status").then((r) => setWhatsAppStatus(r.data)).catch(() => setWhatsAppStatus({ configured: false }));
  }, [shareOpen]);

  const getPdf = async () => {
    if (pdfFile) return pdfFile;
    if (pendingPdf.current) return pendingPdf.current;
    throw new Error("Document PDF is not ready. Please reload and retry.");
  };

  const downloadPdf = async () => {
    try {
      const file = await getPdf();
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err.message); }
  };

  const printPdf = () => {
    pdfPreviewRef.current?.print();
  };

  const shareWithWhatsAppApp = async () => {
    try {
      const file = await getPdf();
      if (!navigator.share || (navigator.canShare && !navigator.canShare({ files: [file] }))) {
        toast.error("Is browser se PDF file-share available nahi hai. WhatsApp Desktop install karein ya Settings me WhatsApp Business connect karein.");
        return;
      }
      await navigator.share({
        files: [file],
        title: `${documentLabel(kind, doc)} ${documentNumber(doc)}`,
        text: `${documentLabel(kind, doc)} ${documentNumber(doc)} - ${doc.seller?.trade_name || doc.seller?.name || "Company"}`,
      });
      setShareOpen(false);
    } catch (err) {
      if (err?.name !== "AbortError") toast.error(err?.message || "WhatsApp PDF share nahi ho paaya");
    }
  };

  const sendWhatsAppDirect = async () => {
    const phone = whatsAppTo.replace(/\D/g, "");
    if (phone.length < 8) return toast.error("Recipient WhatsApp number country code ke saath enter karo");
    if (!whatsAppStatus?.configured) return toast.error("Settings me WhatsApp Business direct sending connect karo");
    setSendingWhatsApp(true);
    try {
      const data = new FormData();
      data.append("kind", kind);
      data.append("doc_id", id);
      data.append("to", phone);
      data.append("copy_label", copy);
      await api.post("/documents/whatsapp", data);
      toast.success(`PDF WhatsApp par ${phone} ko bhej diya`);
      setShareOpen(false);
    } catch (err) {
      toast.error(err?.message || "WhatsApp send failed");
    }
    setSendingWhatsApp(false);
  };

  const connectOutlook = async () => {
    try {
      const { data } = await api.post("/mail/outlook/connect");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.message || "Outlook connection start nahi ho paaya");
    }
  };

  const sendEmail = async (mode = emailMode) => {
    if (!emailTo.trim()) return toast.error("Recipient email enter karo");
    if (mode === "gmail" && !(mailStatus?.configured && mailStatus?.provider === "gmail")) {
      return toast.error("Settings me company Gmail connect karo");
    }
    if (mode === "outlook" && !mailStatus?.outlook_connected) {
      return connectOutlook();
    }
    if (mode === "direct" && !mailStatus?.direct_mode) {
      return toast.error("Pehle Gmail ya Outlook connect karo");
    }
    setSendingEmail(true);
    try {
      const data = new FormData();
      data.append("kind", kind);
      data.append("doc_id", id);
      data.append("to", emailTo.trim());
      data.append("subject", emailSubject);
      data.append("message_text", emailMessage);
      data.append("mode", mode);
      data.append("copy_label", copy);
      const { data: sent } = await api.post("/documents/email", data);
      toast.success(`PDF sent from ${sent.from} to ${sent.to}`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(err?.message || "Email send failed");
    }
    setSendingEmail(false);
  };

  if (loadError) return <div role="alert" className="p-20 text-center text-rose-700">{loadError}</div>;
  if (!doc) return <div className="p-20 text-center text-sm text-slate-500">Loading document...</div>;

  const isSale = kind === "sale";

  return (
    <div className="min-h-screen py-6" style={{ background: "#EEF1F8" }}>
      <div className="no-print w-[210mm] max-w-[calc(100vw-2rem)] mx-auto mb-4 flex justify-between items-center px-1">
        <Link to={KIND_TO_BACK[kind] || "/sales"} data-testid="print-back-btn"
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {((kind === "sale" && can(user, "invoice", "full")) || (kind === "purchase" && can(user, "purchase", "full")) || (kind === "quotation" && can(user, "quotation", "full"))) && (
            <Link to={kind === "sale" ? `/sales/${id}/edit` : kind === "purchase" ? `/purchases/${id}/edit` : `/quotations/${id}/edit`}
              data-testid="edit-document-btn"
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
              <Pencil className="h-4 w-4" /> Edit
            </Link>
          )}
          {isSale && (
            <select value={copy} onChange={(e) => setCopy(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-2 py-2 bg-white text-slate-600">
              <option>Original for Recipient</option>
              <option>Duplicate for Transporter</option>
              <option>Triplicate for Supplier</option>
            </select>
          )}
          <button data-testid="email-pdf-btn" onClick={() => setEmailOpen(true)}
            className="flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-md bg-white border border-slate-300 text-slate-700 hover:border-teal-500 hover:text-teal-700 transition-colors">
            <Mail className="h-4 w-4" /> Email PDF
          </button>
          <button data-testid="whatsapp-share-btn" onClick={() => setShareOpen(true)}
            className="flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
            <MessageCircle className="h-4 w-4" /> WhatsApp PDF
          </button>
          <button data-testid="download-pdf-btn" disabled={!pdfFile} onClick={downloadPdf}
            className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-md border border-slate-300 bg-white disabled:opacity-50">
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button data-testid="print-btn" disabled={!previewUrl} onClick={printPdf}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md text-white transition-colors"
            style={{ background: DOC.deepBlue }}>
            <Printer className="h-4 w-4" /> Print PDF
          </button>
        </div>
      </div>

      {shareOpen && (
        <div className="no-print fixed inset-0 z-50 bg-slate-950/45 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShareOpen(false); }}>
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900">WhatsApp PDF</h3>
                <p className="text-xs text-slate-500 mt-1">Ab PDF manually download/choose nahi karna. Software isi visible document ka PDF khud banayega.</p>
              </div>
              <button onClick={() => setShareOpen(false)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="h-4 w-4" /></button>
            </div>

            {whatsAppStatus?.configured ? (
              <div className="mt-4">
                <label className="sgp-label">Recipient WhatsApp number</label>
                <input className="sgp-input font-mono" value={whatsAppTo} onChange={(e) => setWhatsAppTo(e.target.value)}
                  placeholder="e.g. 919876543210" />
                <p className="text-[12px] text-slate-500 mt-1">Country code ke saath. Connected business number: {whatsAppStatus.display_phone || "configured"}.</p>
                <button onClick={sendWhatsAppDirect} disabled={sendingWhatsApp || generatingPdf}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                  {(sendingWhatsApp || generatingPdf) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {generatingPdf ? "Preparing PDF..." : sendingWhatsApp ? "Sending..." : "Send PDF directly"}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold text-emerald-900">No download step</p>
                  <p className="text-[12px] text-emerald-800 mt-1">WhatsApp Desktop installed hai to system share se PDF file directly WhatsApp ko de sakte ho.</p>
                </div>
                <button onClick={shareWithWhatsAppApp} disabled={generatingPdf}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                  {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                  {generatingPdf ? "Preparing PDF..." : "Share PDF with WhatsApp app"}
                </button>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-[12px] text-slate-600">Specific WhatsApp chat ko bina Windows share panel ke one-click PDF bhejne ke liye <b>Settings → WhatsApp Business</b> connect karo.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {emailOpen && (
        <div className="no-print fixed inset-0 z-50 bg-slate-950/45 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setEmailOpen(false); }}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900">Email PDF</h3>
                <p className="text-xs text-slate-500 mt-1">PDF software khud banayega. Bas receiver ka email dalo aur sending method choose karo.</p>
              </div>
              <button onClick={() => setEmailOpen(false)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="h-4 w-4" /></button>
            </div>

            <div className="mt-4">
              <label className="sgp-label">To</label>
              <input type="email" className="sgp-input" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@example.com" />
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={() => setEmailMode("direct")}
                className={`rounded-lg border p-3 text-left ${emailMode === "direct" ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white"}`}>
                <p className="text-xs font-bold text-slate-900">1. Direct Send</p>
                <p className="text-[11px] text-slate-500 mt-1">{mailStatus?.direct_from ? `From ${mailStatus.direct_from}` : "Connect a sender"}</p>
              </button>
              <button onClick={() => setEmailMode("gmail")}
                className={`rounded-lg border p-3 text-left ${emailMode === "gmail" ? "border-red-400 bg-red-50" : "border-slate-200 bg-white"}`}>
                <p className="text-xs font-bold text-slate-900">2. Gmail</p>
                <p className="text-[11px] text-slate-500 mt-1">{mailStatus?.provider === "gmail" && mailStatus?.configured ? mailStatus.from : "Company Gmail"}</p>
              </button>
              <button onClick={() => setEmailMode("outlook")}
                className={`rounded-lg border p-3 text-left ${emailMode === "outlook" ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white"}`}>
                <p className="text-xs font-bold text-slate-900">3. Outlook</p>
                <p className="text-[11px] text-slate-500 mt-1">{mailStatus?.outlook_connected ? mailStatus.outlook_from : "Microsoft 365"}</p>
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              {emailMode === "direct" && (
                <p className="text-xs text-slate-600">
                  {mailStatus?.direct_from
                    ? <>From: <b>{mailStatus.direct_from}</b>. Direct Send logged-in user's Outlook ko prefer karta hai; warna connected company mailbox use hota hai.</>
                    : <>Direct sender connected nahi hai. Settings me Gmail connect karo ya Outlook connect karo.</>}
                </p>
              )}
              {emailMode === "gmail" && (
                <p className="text-xs text-slate-600">
                  {mailStatus?.provider === "gmail" && mailStatus?.configured
                    ? <>PDF company Gmail <b>{mailStatus.from}</b> se direct jayega.</>
                    : <>Company Gmail connected nahi hai. Settings → Document Email me Gmail connect karo.</>}
                </p>
              )}
              {emailMode === "outlook" && (
                <p className="text-xs text-slate-600">
                  {mailStatus?.outlook_connected
                    ? <>PDF logged-in Microsoft user <b>{mailStatus.outlook_from}</b> se Microsoft Graph ke through direct jayega.</>
                    : <>Outlook connected nahi hai. Neeche Connect Outlook dabao; Microsoft login/consent ke baad yahi account sender banega.</>}
                </p>
              )}
            </div>

            {emailMode === "outlook" && !mailStatus?.outlook_connected ? (
              <button onClick={connectOutlook}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800">
                <ExternalLink className="h-4 w-4" /> Connect Outlook / Microsoft 365
              </button>
            ) : (
              <button onClick={() => sendEmail(emailMode)} disabled={sendingEmail || generatingPdf}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 disabled:opacity-50">
                {(sendingEmail || generatingPdf) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {generatingPdf ? "Preparing PDF..." : sendingEmail ? "Sending..." : "Send PDF"}
              </button>
            )}
          </div>
        </div>
      )}

      <div id="print-area" data-testid="print-area" className="w-full max-w-6xl mx-auto px-4">
        {generatingPdf && <p role="status" className="p-12 text-center">Preparing official PDF...</p>}
        {pdfError && <p role="alert" className="p-12 text-center text-rose-700">{pdfError}</p>}
        {pdfFile && <Suspense fallback={<p role="status" className="p-8 text-center">Loading PDF preview...</p>}><PdfPreview ref={pdfPreviewRef} file={pdfFile} /></Suspense>}
      </div>
    </div>
  );
}
